import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, LogOut, X } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { useLogout } from "@/hooks/useLogout";
import { useAdminNavBadges } from "@/hooks/queries";
import { activeGroupPaths, adminNav, isItemActive, type NavBadgeKey, type NavItem } from "./adminNav";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { SheetClose } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface AdminSidebarProps {
  collapsed: boolean;
  /** Render inside the mobile sheet: static, full width, with a close button. */
  mobile?: boolean;
  /** Called after a link is chosen (used to close the mobile sheet). */
  onNavigate?: () => void;
}

// Left padding (px) for nested links/submenus: first nested level, then each level deeper.
const INDENT_BASE = 36;
const INDENT_STEP = 12;
const nestedPadding = (depth: number) => ({ paddingLeft: INDENT_BASE + (depth - 1) * INDENT_STEP });

const AdminSidebar = ({ collapsed: collapsedProp, mobile = false, onNavigate }: AdminSidebarProps) => {
  // Collapsed rail expands as an overlay while hovered; page content keeps its collapsed offset.
  const [hovered, setHovered] = useState(false);
  const collapsed = collapsedProp && !hovered;
  const { t } = useTranslation();
  const location = useLocation();
  const logout = useLogout("/auth/admin/secure/login");
  const { pendingStoreApprovals, pendingProductApprovals } = useAdminNavBadges();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const translateTitle = (title: string) => {
    switch (title) {
      case "Dashboard": return t("nav.dashboard");
      case "Stores": return t("nav.stores");
      case "Products": return t("nav.products");
      case "Categories": return t("nav.categories");
      case "Orders": return t("nav.orders");
      case "Notifications": return t("nav.notifications");
      case "Users": return t("nav.users");
      case "Delivery": return t("nav.delivery");
      case "KYC Approvals": return t("nav.kyc");
      case "Wallet": return t("nav.wallet");
      case "Payouts & Adjust": return t("nav.payouts");
      case "Transactions": return t("nav.wallet_transactions");
      case "Settings": return t("nav.settings");
      case "Platform": return t("nav.settings_platform");
      case "Referral Levels": return t("nav.referrals");
      case "Email": return t("nav.email");
      case "Integrations": return t("nav.integrations");
      case "My Profile": return t("nav.profile");
      case "CMS Pages": return t("nav.cms");
      case "Banners": return t("nav.banners");
      case "Coupons": return t("nav.coupons");
      case "Rank Rewards": return "Rank Rewards";
      case "Completed Rewards": return "Completed Rewards";
      case "Reports": return "Reports";
      case "All Summary": return "All Summary";
      case "Direct Income": return "Direct Income";
      case "Level Income": return "Level Income";
      case "Reward Income": return "Reward Income";
      case "Cashback Income": return "Cashback Income";
      default: return title;
    }
  };

  const translateSection = (secId: string, defaultLabel: string) => {
    switch (secId) {
      case "main": return t("nav.section_main");
      case "content": return t("nav.section_content");
      case "management": return t("nav.section_manage");
      case "finance": return t("nav.section_finance");
      case "settings": return t("nav.section_settings");
      default: return defaultLabel;
    }
  };

  const getBadge = (badgeKey?: NavBadgeKey) => {
    if (badgeKey === "stores") return pendingStoreApprovals || undefined;
    if (badgeKey === "products") return pendingProductApprovals || undefined;
    return undefined;
  };

  useEffect(() => {
    const paths = adminNav.flatMap((section) => activeGroupPaths(location.pathname, section.items));
    if (paths.length === 0) return;
    setOpenGroups((prev) => ({ ...prev, ...Object.fromEntries(paths.map((path) => [path, true])) }));
  }, [location.pathname]);

  const tooltipClass =
    "bg-neutral-900 text-white text-xs font-medium border border-neutral-700 shadow-xl z-[60] px-3 py-1.5 rounded-lg";

  /** A single link. depth 0 = top level (icon, tooltip when collapsed); deeper = nested (dot, indented). */
  const renderLink = (item: NavItem, depth: number) => {
    const Icon = item.icon;
    const nested = depth > 0;
    const badge = getBadge(item.badgeKey);
    const translatedTitle = translateTitle(item.title);

    const content = (
      <NavLink
        to={item.url!}
        onClick={onNavigate}
        end={item.end}
        style={nested ? nestedPadding(depth) : undefined}
        className={cn(
          "group relative flex items-center gap-3 rounded-lg py-2 text-[13px] font-medium transition-all duration-200",
          "text-neutral-400 hover:text-white hover:bg-white/10",
          nested ? "pr-3" : "px-3",
          !nested && collapsed && "justify-center w-10 h-10 mx-auto px-0"
        )}
        activeClassName={
          item.alias
            ? undefined
            : cn(
                "bg-gradient-primary text-primary-foreground shadow-md hover:text-primary-foreground",
                !nested && collapsed && "shadow-glow"
              )
        }
      >
        {Icon ? (
          <Icon className={cn("h-[18px] w-[18px] shrink-0", !nested && collapsed && "h-5 w-5")} />
        ) : (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-600 group-hover:bg-neutral-400" />
        )}
        {(!collapsed || nested) && (
          <>
            <span className="flex-1 truncate">{translatedTitle}</span>
            {badge ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                {badge}
              </span>
            ) : null}
          </>
        )}
        {!nested && collapsed && badge ? (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground ring-2 ring-black">
            {badge}
          </span>
        ) : null}
      </NavLink>
    );

    if (collapsed && !nested) {
      return (
        <Tooltip key={item.title} delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} className={tooltipClass}>
            {translatedTitle}
            {badge ? <span className="ml-1.5 text-destructive-foreground bg-destructive rounded-full px-1 text-[9px]">{badge}</span> : null}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.title}>{content}</div>;
  };

  /** Collapsed-rail flyout body: links, with sub-submenus shown as indented labelled blocks. */
  const renderFlyoutItems = (items: NavItem[], level = 0) =>
    items.map((item) =>
      item.children ? (
        <div key={item.title} className="pt-1">
          <p className="px-2.5 pb-0.5 pt-1 text-[11px] font-semibold text-neutral-500">
            {translateTitle(item.title)}
          </p>
          <div className="space-y-0.5 border-l border-white/10 pl-1.5 ml-2.5">
            {renderFlyoutItems(item.children, level + 1)}
          </div>
        </div>
      ) : item.url ? (
        <NavLink
          key={item.url}
          to={item.url}
          onClick={onNavigate}
          end={item.end}
          className="flex items-center rounded-lg px-2.5 py-2 text-[13px] font-medium text-neutral-300 transition-all hover:bg-white/10 hover:text-white"
          activeClassName={item.alias ? undefined : "bg-gradient-primary text-primary-foreground hover:text-primary-foreground"}
        >
          {translateTitle(item.title)}
        </NavLink>
      ) : null,
    );

  /** A submenu. depth 0 = top level (icon; popover flyout when collapsed); deeper = indented collapsible. */
  const renderGroup = (item: NavItem, depth: number, path: string) => {
    const groupActive = isItemActive(location.pathname, item);
    const isOpen = openGroups[path] ?? groupActive;
    const Icon = item.icon;

    if (collapsed) {
      return (
        <Popover key={path}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={translateTitle(item.title)}
              className={cn(
                "relative mx-auto flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200",
                "text-neutral-400 hover:bg-white/10 hover:text-white",
                groupActive && "bg-gradient-primary text-primary-foreground shadow-glow hover:text-primary-foreground"
              )}
            >
              {Icon ? <Icon className="h-5 w-5" /> : null}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={8}
            collisionPadding={8}
            className="z-[60] max-h-[calc(100dvh-1rem)] w-56 overflow-y-auto rounded-xl border border-neutral-700 bg-neutral-900 p-1.5 text-white shadow-xl"
          >
            <p className="px-2.5 pb-1 pt-0.5 text-[11px] font-semibold text-neutral-500">
              {translateTitle(item.title)}
            </p>
            <div className="space-y-0.5">{renderFlyoutItems(item.children ?? [])}</div>
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Collapsible
        key={path}
        open={isOpen}
        onOpenChange={(open) => setOpenGroups((prev) => ({ ...prev, [path]: open }))}
        className="space-y-0.5"
      >
        <CollapsibleTrigger
          style={depth > 0 ? nestedPadding(depth) : undefined}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg py-2 text-[13px] font-medium transition-all duration-200",
            "text-neutral-400 hover:text-white hover:bg-white/10",
            depth > 0 ? "pr-3" : "px-3",
            groupActive && "text-white"
          )}
        >
          {Icon ? <Icon className="h-[18px] w-[18px] shrink-0" /> : null}
          <span className="flex-1 truncate text-left">{translateTitle(item.title)}</span>
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-neutral-500 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-0.5">
          {item.children?.map((child) => renderItem(child, depth + 1, path))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const renderItem = (item: NavItem, depth: number, parentPath = ""): React.ReactNode => {
    const path = parentPath ? `${parentPath}/${item.title}` : item.title;
    if (item.children) return renderGroup(item, depth, path);
    return item.url ? renderLink(item, depth) : null;
  };

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        "glass-sidebar flex flex-col",
        mobile
          ? "h-full w-full"
          : "fixed left-0 top-0 z-30 h-screen transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        !mobile && (collapsed ? "w-[68px]" : "w-[216px]"),
        !mobile && collapsedProp && hovered && "z-40 shadow-2xl shadow-black/40"
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-white/10",
          collapsed ? "justify-center px-2" : "px-4"
        )}
      >
        <Link
          to="/admin"
          className={cn(
            "flex items-center gap-2.5",
            collapsed ? "justify-center" : "min-w-0 flex-1"
          )}
        >
          {collapsed ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm">
              <img src="/icon.png" alt="Icon" className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm">
                <img src="/icon.png" alt="Icon" className="h-full w-full object-contain" />
              </div>
              <div className="flex flex-col">
                <div className="rounded-lg bg-white px-2 py-0.5 flex items-center justify-center h-7">
                  <img src="/nav-logo.png" alt="Bihari Mall" className="h-5 object-contain" />
                </div>
                
              </div>
            </div>
          )}
        </Link>
        {mobile && (
          <SheetClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-lg text-neutral-400 hover:bg-white/10 hover:text-white"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>
          </SheetClose>
        )}
      </div>

      {/* Navigation */}
      <nav className="scrollbar-hover flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-4">
        {adminNav.map((section) => {
          if (section.items.length === 0) return null;

          return (
            <div key={section.id} className="space-y-0.5">
              {!collapsed ? (
                <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  {translateSection(section.id, section.label)}
                </p>
              ) : (
                <div className="mx-auto my-1 w-6 border-t border-white/10" />
              )}
              {section.items.map((item) => renderItem(item, 0))}
            </div>
          );
        })}
      </nav>

      {/* Sign out */}
      <div className="shrink-0 border-t border-white/10 p-2.5">
        {collapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
              onNavigate?.();
              logout();
            }}
                className="flex h-10 w-10 mx-auto items-center justify-center rounded-lg text-neutral-400 transition-all hover:bg-red-500/10 hover:text-red-400"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="bg-neutral-900 text-white text-xs font-medium border border-neutral-700 shadow-xl z-[60] px-3 py-1.5 rounded-lg">
              {t("logout")}
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => {
              onNavigate?.();
              logout();
            }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium text-neutral-400 transition-all hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            <span>{t("logout")}</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default AdminSidebar;
